import { getAmountsOut } from "../../utils/common";
import { addToast } from "@heroui/react";
import { parseUnits, encodeFunctionData } from "viem";
import { rainbowKitConfig } from "../../utils/config";
import { waitForTransactionReceipt, writeContract } from "@wagmi/core";
import { TokenSwapApi, writeContractApi } from "../../api/methods-marketplace";

const ROUTER_ABI = [
  {
    name: "swapExactETHForTokens",
    type: "function",
    stateMutability: "payable",
    inputs: [
      { name: "amountOutMin", type: "uint256" },
      { name: "path", type: "address[]" },
      { name: "to", type: "address" },
      { name: "deadline", type: "uint256" }
    ],
    outputs: [{ name: "amounts", type: "uint256[]" }]
  },
  {
    name: "swapExactTokensForETH",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "amountIn", type: "uint256" },
      { name: "amountOutMin", type: "uint256" },
      { name: "path", type: "address[]" },
      { name: "to", type: "address" },
      { name: "deadline", type: "uint256" }
    ],
    outputs: [{ name: "amounts", type: "uint256[]" }]
  },
  {
    name: "swapExactTokensForTokens",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "amountIn", type: "uint256" },
      { name: "amountOutMin", type: "uint256" },
      { name: "path", type: "address[]" },
      { name: "to", type: "address" },
      { name: "deadline", type: "uint256" }
    ],
    outputs: [{ name: "amounts", type: "uint256[]" }]
  }
];

const ERC20_ABI = [
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" }
    ],
    outputs: [{ name: "", type: "bool" }]
  }
];

const swapTokens = async (
  amount = "0",
  fromToken,
  toToken,
  address,
  selectedTokenSymbol,
  selectedTokenToSymbol,
  convertedAmount,
  token,
  blockNumber,
  transactionHash,
  setStatus = () => {},
  setTransactionHash = () => {},
  setBlockNumber = () => {},
  getSwapHistory = () => {},
  isCustodialWallet = false,
  publicClient = null
) => {
  try {
    const TESTNET_ROUTER_ADDRESS = process.env.REACT_APP_TESTNET_ROUTER_ADDRESS;
    const WBNB_TESTNET = process.env.REACT_APP_WBNB_TESTNET.toLowerCase();

    const amountIn = parseUnits(amount.toString(), 18);

    const expectedOut = await getAmountsOut(amount, fromToken, toToken);
    const expectedOutWei = parseUnits(expectedOut.toString(), 18);

    const slippage = 5;
    const minAmountWei = (expectedOutWei * BigInt(100 - slippage)) / 100n;

    const path = [fromToken, toToken];
    const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 20);

    setStatus("🔄 Preparing swap...");

    if (isCustodialWallet) {
      if (fromToken.toLowerCase() === WBNB_TESTNET) {
        const swapCallData = encodeFunctionData({
          abi: ROUTER_ABI,
          functionName: "swapExactTokensForETH",
          args: [amountIn, minAmountWei, path, address, deadline]
        });

        const swapResult = await writeContractApi({
          address: TESTNET_ROUTER_ADDRESS,
          encoded_data: swapCallData
        });

        if (!swapResult?.data?.tx_hash) {
          throw new Error("Transaction hash not received");
        }

        const txHash = swapResult.data.tx_hash;
        setStatus("⏳ Waiting for confirmation...");

        // Wait and check actual receipt
        if (!publicClient) {
          throw new Error(
            "publicClient is required for custodial wallet swaps"
          );
        }

        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash
        });

        if (receipt.status !== "success") {
          throw new Error("Swap transaction failed on blockchain");
        }

        const blockNum = Number(receipt.blockNumber);
        setTransactionHash(txHash);
        setBlockNumber(blockNum);
        setStatus(`✅ Swapped ${amount} WBNB to BNB`);

        return { success: 200, txHash, blockNumber: blockNum };
      }

      // Token to Token swap - requires approval first
      setStatus("🔐 Approving token...");

      const approveCallData = encodeFunctionData({
        abi: ERC20_ABI,
        functionName: "approve",
        args: [TESTNET_ROUTER_ADDRESS, amountIn]
      });

      const approveResult = await writeContractApi({
        address: fromToken,
        encoded_data: approveCallData
      });

      if (!approveResult?.data?.tx_hash) {
        throw new Error("Approval transaction hash not received");
      }

      if (!publicClient) {
        throw new Error("publicClient is required for custodial wallet swaps");
      }

      // Wait for approval receipt
      const approvalReceipt = await publicClient.waitForTransactionReceipt({
        hash: approveResult.data.tx_hash
      });

      if (approvalReceipt.status !== "success") {
        throw new Error("Approval transaction failed on blockchain");
      }

      setStatus("🔄 Swapping tokens...");

      // Execute swap
      const swapCallData = encodeFunctionData({
        abi: ROUTER_ABI,
        functionName: "swapExactTokensForTokens",
        args: [amountIn, minAmountWei, path, address, deadline]
      });

      const swapResult = await writeContractApi({
        address: TESTNET_ROUTER_ADDRESS,
        encoded_data: swapCallData
      });

      if (!swapResult?.data?.tx_hash) {
        throw new Error("Swap transaction hash not received");
      }

      const txHash = swapResult?.data?.tx_hash;
      setStatus("⏳ Waiting for confirmation...");

      const swapReceipt = await publicClient.waitForTransactionReceipt({
        hash: txHash
      });

      console.log("swap Receipt", swapReceipt.status);

      if (
        swapReceipt.status !== "success" &&
        swapReceipt.status !== "swap Receipt reverted"
      ) {
        addToast({
          title: "Swap Failed",
          description: "Oops... Token Swap Failed",
          color: "warning",
          variant: "bordered"
        });
        setStatus("❌ Transaction failed");
        return {
          success: 400,
          error: "Transaction reverted or failed on-chain"
        };
      }

      const blockNum = Number(swapReceipt.blockNumber);
      setTransactionHash(txHash);
      setBlockNumber(blockNum);
      setStatus(`✅ Swapped ${amount} tokens successfully`);

      return { success: 200, txHash, blockNumber: blockNum };
    } else {
      if (fromToken.toLowerCase() === WBNB_TESTNET) {
        const txHash = await writeContract(rainbowKitConfig, {
          address: TESTNET_ROUTER_ADDRESS,
          abi: ROUTER_ABI,
          functionName: "swapExactTokensForETH",
          args: [amountIn, minAmountWei, path, address, deadline]
        });

        setStatus("⏳ Waiting for confirmation...");

        const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
          hash: txHash
        });

        setTransactionHash(txHash);
        setBlockNumber(Number(receipt.blockNumber));
        setStatus(`✅ Swapped ${amount} WBNB to BNB`);

        return {
          success: 200,
          txHash,
          blockNumber: Number(receipt.blockNumber)
        };
      }

      setStatus("🔐 Approving token...");

      await writeContract(rainbowKitConfig, {
        address: fromToken,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [TESTNET_ROUTER_ADDRESS, amountIn]
      });

      const txHash = await writeContract(rainbowKitConfig, {
        address: TESTNET_ROUTER_ADDRESS,
        abi: ROUTER_ABI,
        functionName: "swapExactTokensForTokens",
        args: [amountIn, minAmountWei, path, address, deadline]
      });

      setStatus("⏳ Waiting for confirmation...");

      const receipt = await waitForTransactionReceipt(rainbowKitConfig, {
        hash: txHash
      });

      setTransactionHash(txHash);
      setBlockNumber(Number(receipt.blockNumber));
      setStatus(`✅ Swapped ${amount} tokens successfully`);

      return { success: 200, txHash, blockNumber: Number(receipt.blockNumber) };
    }
  } catch (err) {
    const isRejected =
      err?.message?.toLowerCase().includes("user rejected") ||
      err?.shortMessage?.toLowerCase().includes("user rejected") ||
      err?.cause?.message?.toLowerCase().includes("user rejected");

    const errorMessage = isRejected
      ? "You rejected the transaction in MetaMask."
      : err?.response?.data?.message ||
        err?.reason ||
        err?.shortMessage ||
        err?.message ||
        "Please try again later";

    setStatus("❌ Transaction failed");

    const updatedData = {
      from_token: fromToken,
      to_token: toToken,
      from_symbol: selectedTokenSymbol,
      to_symbol: selectedTokenToSymbol,
      from_amount: amount,
      to_amount: convertedAmount,
      id: blockNumber,
      transaction_hash: transactionHash ? transactionHash : "",
      status: "Failed",
      swap_rate: parseFloat(convertedAmount) / parseFloat(amount)
    };

    try {
      await TokenSwapApi(updatedData, token);

      addToast({
        title: "Swap History Updated",
        description: "Your swap attempt has been recorded.",
        color: "success",
        variant: "bordered"
      });
    } catch (apiError) {
      addToast({
        title: "History Update Failed",
        description: "Unable to update swap history.",
        color: "error",
        variant: "bordered"
      });
    }

    getSwapHistory();

    addToast({
      title: isRejected ? "Transaction Cancelled" : "Transaction Failed",
      description: errorMessage,
      color: isRejected ? "warning" : "error",
      variant: "bordered"
    });

    console.log("Swap error:", err);

    return { success: false, error: errorMessage };
  }
};

export default swapTokens;
